import { useState } from 'react'
import { Plus, Trash2, CheckCircle2, XCircle, Zap, Mail, MessageSquare, Settings, Wifi, Pencil, X, Save, Loader2, RefreshCw } from 'lucide-react'
import { useEmAccounts, useCreateEmAccount, useUpdateEmAccount, useDeleteEmAccount, useTestEmAccount, useSyncEmAccountFromUserSmtp, useCreateEmAccountFromUserSmtp, useUserSmtpStatus, useEmWaAccounts, useCreateEmWaAccount, useUpdateEmWaAccount, useTestEmWaAccount, type EmAccount, type EmWhatsAppAccount } from '@/hooks/useEmailMarketing'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

type SmtpForm = {
  name: string; email: string; reply_to: string; sender_name: string
  smtp_host: string; smtp_port: number; smtp_user: string; smtp_password: string
  use_tls: boolean; use_ssl: boolean; daily_limit: number; is_default: boolean
  signature_html: string
}
type WaForm = {
  name: string; phone_number: string; instance_id: string; instance_token: string
  client_token: string; daily_limit: number; delay_ms: number
}

const EMPTY_SMTP: SmtpForm = { name: '', email: '', reply_to: '', sender_name: '', smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '', use_tls: true, use_ssl: false, daily_limit: 500, is_default: false, signature_html: '' }
const EMPTY_WA: WaForm = { name: '', phone_number: '', instance_id: '', instance_token: '', client_token: '', daily_limit: 1000, delay_ms: 3000 }

export default function EmAccountsPage() {
  const [tab, setTab] = useState<'smtp' | 'whatsapp'>('smtp')
  const accounts = useEmAccounts()
  const waAccounts = useEmWaAccounts()
  const createMut = useCreateEmAccount()
  const updateMut = useUpdateEmAccount()
  const deleteMut = useDeleteEmAccount()
  const testMut = useTestEmAccount()
  const syncMut = useSyncEmAccountFromUserSmtp()
  const createFromUserSmtpMut = useCreateEmAccountFromUserSmtp()
  const userSmtp = useUserSmtpStatus()
  const createWaMut = useCreateEmWaAccount()
  const updateWaMut = useUpdateEmWaAccount()
  const testWaMut = useTestEmWaAccount()
  const [syncingId, setSyncingId] = useState<string | null>(null)

  // Modal state
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<EmAccount | null>(null)
  const [smtpForm, setSmtpForm] = useState<SmtpForm>(EMPTY_SMTP)
  const [waForm, setWaForm] = useState<WaForm>(EMPTY_WA)
  const [testResult, setTestResult] = useState<{ status: string; message?: string } | null>(null)
  const [testing, setTesting] = useState(false)
  const [saving, setSaving] = useState(false)

  // Open modal for new account
  const openNew = () => {
    setEditingId(null)
    setSmtpForm(EMPTY_SMTP)
    setWaForm(EMPTY_WA)
    setTestResult(null)
    setShowModal(true)
  }

  // Open modal for editing SMTP
  const openEditSmtp = (a: EmAccount) => {
    setEditingId(a.id)
    setSmtpForm({
      name: a.name, email: a.email, reply_to: a.reply_to || '', sender_name: a.sender_name,
      smtp_host: a.smtp_host, smtp_port: a.smtp_port, smtp_user: a.smtp_user, smtp_password: '',
      use_tls: a.use_tls, use_ssl: a.use_ssl, daily_limit: a.daily_limit, is_default: a.is_default,
      signature_html: a.signature_html || '',
    })
    setTestResult(null)
    setShowModal(true)
  }

  // Open modal for editing WA
  const openEditWa = (a: EmWhatsAppAccount) => {
    setEditingId(a.id)
    setWaForm({
      name: a.name, phone_number: a.phone_number || '', instance_id: a.instance_id,
      instance_token: '', client_token: '', daily_limit: a.daily_limit, delay_ms: a.delay_ms,
    })
    setTestResult(null)
    setShowModal(true)
  }

  // Test connection
  const handleTest = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      if (tab === 'smtp') {
        if (editingId) {
          // Update password if changed, then test existing
          if (smtpForm.smtp_password) {
            await updateMut.mutateAsync({ id: editingId, smtp_password: smtpForm.smtp_password })
          }
          const result = await testMut.mutateAsync(editingId)
          setTestResult(result as any)
        } else {
          // For new account: save first, then test
          const created = await createMut.mutateAsync(smtpForm) as any
          setEditingId(created.id)
          const result = await testMut.mutateAsync(created.id)
          setTestResult(result as any)
        }
      } else {
        if (editingId) {
          const result = await testWaMut.mutateAsync(editingId)
          setTestResult(result as any)
        } else {
          const created = await createWaMut.mutateAsync(waForm) as any
          setEditingId(created.id)
          const result = await testWaMut.mutateAsync(created.id)
          setTestResult(result as any)
        }
      }
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Erro ao testar'
      setTestResult({ status: 'error', message: msg })
    } finally {
      setTesting(false)
    }
  }

  // Save account
  const handleSave = async () => {
    setSaving(true)
    try {
      if (tab === 'smtp') {
        if (editingId) {
          const payload: any = { ...smtpForm }
          if (!payload.smtp_password) delete payload.smtp_password // don't overwrite if empty
          await updateMut.mutateAsync({ id: editingId, ...payload })
        } else {
          await createMut.mutateAsync(smtpForm)
        }
      } else {
        if (editingId) {
          const payload: any = { ...waForm }
          // Only send token fields if user typed a new value (not empty placeholder)
          if (!payload.instance_token) delete payload.instance_token
          if (!payload.client_token) delete payload.client_token
          await updateWaMut.mutateAsync({ id: editingId, ...payload })
        } else {
          await createWaMut.mutateAsync(waForm)
        }
      }
      setShowModal(false)
    } catch (e: any) {
      alert('Erro ao salvar: ' + (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : e?.message || JSON.stringify(e?.response?.data || e)))
    } finally {
      setSaving(false)
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingId(null)
    setTestResult(null)
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Contas de Envio</h1>
        <button onClick={openNew} className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded-lg flex items-center gap-2 hover:opacity-90">
          <Plus className="w-4 h-4" /> Nova Conta
        </button>
      </div>

      {/* Tab */}
      <div className="flex gap-1 bg-muted/50 p-1 rounded-lg w-fit">
        <button onClick={() => setTab('smtp')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${tab === 'smtp' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}>
          <Mail className="w-4 h-4" /> SMTP ({(accounts.data || []).length})
        </button>
        <button onClick={() => setTab('whatsapp')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${tab === 'whatsapp' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}>
          <MessageSquare className="w-4 h-4" /> WhatsApp ({(waAccounts.data || []).length})
        </button>
      </div>

      {/* SMTP Accounts Grid */}
      {tab === 'smtp' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(accounts.data || []).map(a => (
            <div key={a.id} className="bg-card border rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    {a.name}
                    {a.is_default && <span className="text-[10px] bg-primary/15 text-primary px-2 py-0.5 rounded-full">Padrão</span>}
                  </h3>
                  <p className="text-xs text-muted-foreground">{a.email}</p>
                </div>
                <span className={`w-2 h-2 rounded-full mt-1 ${a.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5 mb-3">
                <p><strong>Remetente:</strong> {a.sender_name}</p>
                <p><strong>Servidor:</strong> {a.smtp_host}:{a.smtp_port} ({a.use_tls ? 'TLS' : a.use_ssl ? 'SSL' : 'None'})</p>
                <p><strong>Usuário:</strong> {a.smtp_user}</p>
                <p><strong>Enviados hoje:</strong> {a.sent_today} / {a.daily_limit}</p>
              </div>
              <div className="flex gap-1 flex-wrap">
                <button onClick={() => openEditSmtp(a)} className="px-2.5 py-1.5 text-xs border rounded-lg hover:bg-accent flex items-center gap-1">
                  <Pencil className="w-3 h-3" /> Editar
                </button>
                <button
                  onClick={async () => {
                    if (!userSmtp.data?.configured) {
                      alert('Você ainda não configurou o SMTP em Configurações → SMTP.')
                      return
                    }
                    if (!confirm(`Substituir o SMTP da conta "${a.name}" pelo SMTP de Configurações (${userSmtp.data.smtp_username})?`)) return
                    setSyncingId(a.id)
                    try {
                      await syncMut.mutateAsync(a.id)
                    } catch (e: any) {
                      alert('Erro ao sincronizar: ' + (e?.response?.data?.detail || e?.message))
                    } finally {
                      setSyncingId(null)
                    }
                  }}
                  disabled={syncingId === a.id || !userSmtp.data?.configured}
                  title={userSmtp.data?.configured ? 'Copia host/porta/usuário/senha do SMTP de Configurações' : 'Configure seu SMTP em Configurações primeiro'}
                  className="px-2.5 py-1.5 text-xs border rounded-lg hover:bg-accent flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {syncingId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Sincronizar com SMTP de Configurações
                </button>
                <button onClick={() => setConfirmDelete(a)} className="px-2.5 py-1.5 text-xs border rounded-lg hover:bg-accent flex items-center gap-1 text-red-500">
                  <Trash2 className="w-3 h-3" /> Excluir
                </button>
              </div>
            </div>
          ))}
          {(accounts.data || []).length === 0 && (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              Nenhuma conta SMTP cadastrada
              {userSmtp.data?.configured && (
                <div className="mt-3">
                  <button
                    onClick={async () => {
                      const name = prompt('Nome da conta:', userSmtp.data?.smtp_from_name || 'Minha conta')
                      if (!name) return
                      try {
                        await createFromUserSmtpMut.mutateAsync({ name, is_default: true })
                      } catch (e: any) {
                        alert('Erro: ' + (e?.response?.data?.detail || e?.message))
                      }
                    }}
                    className="px-3 py-2 text-sm border rounded-lg hover:bg-accent inline-flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" /> Criar a partir do SMTP de Configurações
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* WhatsApp Accounts Grid */}
      {tab === 'whatsapp' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(waAccounts.data || []).map(a => (
            <div key={a.id} className="bg-card border rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-sm">{a.name}</h3>
                  <p className="text-xs text-muted-foreground">{a.phone_number || 'Sem número vinculado'}</p>
                </div>
                <span className={`flex items-center gap-1 text-xs ${a.is_connected ? 'text-green-500' : 'text-red-500'}`}>
                  <Wifi className="w-3 h-3" /> {a.is_connected ? 'Conectado' : 'Desconectado'}
                </span>
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5 mb-3">
                <p><strong>Instance:</strong> {a.instance_id}</p>
                <p><strong>Delay:</strong> {a.delay_ms}ms | <strong>Enviados:</strong> {a.sent_today}/{a.daily_limit}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEditWa(a)} className="px-2.5 py-1.5 text-xs border rounded-lg hover:bg-accent flex items-center gap-1">
                  <Pencil className="w-3 h-3" /> Editar
                </button>
              </div>
            </div>
          ))}
          {(waAccounts.data || []).length === 0 && <div className="col-span-full text-center py-12 text-muted-foreground">Nenhuma conta Z-API cadastrada</div>}
        </div>
      )}

      {/* ═══ MODAL: New / Edit Account ═══ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative w-full max-w-xl max-h-[90vh] bg-card border rounded-xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <h3 className="font-semibold flex items-center gap-2">
                {tab === 'smtp' ? <Mail className="w-4 h-4 text-primary" /> : <MessageSquare className="w-4 h-4 text-green-500" />}
                {editingId ? 'Editar' : 'Nova'} Conta {tab === 'smtp' ? 'SMTP' : 'WhatsApp (Z-API)'}
              </h3>
              <button onClick={closeModal} className="p-1.5 rounded-lg hover:bg-accent"><X className="w-5 h-5" /></button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4">
              {tab === 'smtp' ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Nome da conta *</label>
                      <input value={smtpForm.name} onChange={e => setSmtpForm({...smtpForm, name: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="Gmail Comercial" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Email (From) *</label>
                      <input value={smtpForm.email} onChange={e => setSmtpForm({...smtpForm, email: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="contato@empresa.com" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Nome do Remetente *</label>
                      <input value={smtpForm.sender_name} onChange={e => setSmtpForm({...smtpForm, sender_name: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="Juliano - NAI IT" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Reply-To (opcional)</label>
                      <input value={smtpForm.reply_to} onChange={e => setSmtpForm({...smtpForm, reply_to: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="responda@empresa.com" />
                    </div>
                  </div>

                  <div className="pt-2 border-t">
                    <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Servidor SMTP</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Host *</label>
                        <input value={smtpForm.smtp_host} onChange={e => setSmtpForm({...smtpForm, smtp_host: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="smtp.gmail.com" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Porta *</label>
                        <input type="number" value={smtpForm.smtp_port} onChange={e => setSmtpForm({...smtpForm, smtp_port: +e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Limite/dia</label>
                        <input type="number" value={smtpForm.daily_limit} onChange={e => setSmtpForm({...smtpForm, daily_limit: +e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Usuário SMTP *</label>
                      <input value={smtpForm.smtp_user} onChange={e => setSmtpForm({...smtpForm, smtp_user: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="usuario@gmail.com" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Senha {editingId ? '(deixe vazio para manter)' : '*'}</label>
                      <input type="password" value={smtpForm.smtp_password} onChange={e => setSmtpForm({...smtpForm, smtp_password: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder={editingId ? '********' : 'senha ou app password'} />
                    </div>
                  </div>

                  <div className="flex items-center gap-6 pt-1">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={smtpForm.use_tls} onChange={e => setSmtpForm({...smtpForm, use_tls: e.target.checked, use_ssl: e.target.checked ? false : smtpForm.use_ssl})} className="rounded" />
                      STARTTLS (porta 587)
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={smtpForm.use_ssl} onChange={e => setSmtpForm({...smtpForm, use_ssl: e.target.checked, use_tls: e.target.checked ? false : smtpForm.use_tls})} className="rounded" />
                      SSL/TLS (porta 465)
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={smtpForm.is_default} onChange={e => setSmtpForm({...smtpForm, is_default: e.target.checked})} className="rounded" />
                      Conta padr&atilde;o
                    </label>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Assinatura HTML (anexada ao final de cada e-mail)</label>
                    <textarea
                      value={smtpForm.signature_html}
                      onChange={e => setSmtpForm({...smtpForm, signature_html: e.target.value})}
                      rows={6}
                      className="w-full mt-1 px-3 py-2 text-xs bg-background border rounded-md font-mono"
                      placeholder='<p>Atenciosamente,<br/><b>Vitor Bragantini</b><br/>Executivo Comercial · NAI<br/>vitor.bragantini@nai-it.com</p>'
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">Cole o HTML da sua assinatura. Voc&ecirc; pode usar vari&aacute;veis Jinja: {`{{ nome }}, {{ empresa }}`}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Nome da conta *</label>
                      <input value={waForm.name} onChange={e => setWaForm({...waForm, name: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="WA Comercial" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Telefone vinculado</label>
                      <input value={waForm.phone_number} onChange={e => setWaForm({...waForm, phone_number: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="+5511999..." />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Instance ID *</label>
                    <input value={waForm.instance_id} onChange={e => setWaForm({...waForm, instance_id: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Instance Token *</label>
                    <input value={waForm.instance_token} onChange={e => setWaForm({...waForm, instance_token: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder={editingId ? '(deixe vazio para manter)' : ''} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Client-Token (opcional)</label>
                    <input value={waForm.client_token} onChange={e => setWaForm({...waForm, client_token: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Limite diário</label>
                      <input type="number" value={waForm.daily_limit} onChange={e => setWaForm({...waForm, daily_limit: +e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Delay entre msgs (ms)</label>
                      <input type="number" value={waForm.delay_ms} onChange={e => setWaForm({...waForm, delay_ms: +e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" />
                    </div>
                  </div>
                </div>
              )}

              {/* Test Result */}
              {testResult && (
                <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 text-sm ${testResult.status === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                  {testResult.status === 'success' ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <XCircle className="w-4 h-4 flex-shrink-0" />}
                  {testResult.status === 'success' ? 'Conexão realizada com sucesso!' : (testResult.message || 'Falha na conexão')}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t flex items-center justify-between flex-shrink-0">
              <button
                onClick={handleTest}
                disabled={testing}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-accent flex items-center gap-2 disabled:opacity-40"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4 text-amber-500" />}
                {testing ? 'Testando...' : 'Testar Conexão'}
              </button>
              <div className="flex gap-2">
                <button onClick={closeModal} className="px-4 py-2 text-sm border rounded-lg hover:bg-accent">Cancelar</button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-40 flex items-center gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir conta SMTP?"
        message={`A conta "${confirmDelete?.name || confirmDelete?.email || ''}" será removida. Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        variant="destructive"
        isLoading={deleteMut.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return
          await deleteMut.mutateAsync(confirmDelete.id)
          setConfirmDelete(null)
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}
